/**
 * ShareStore - MST Store for Public Share Page
 *
 * Standalone store (not in RootStore) for managing public share state.
 * Used on unauthenticated share pages to manage:
 * - Share loading and validation
 * - Folder navigation
 * - Document/folder selection
 * - Download (single and bulk)
 * - Preview with blob URL lifecycle
 */

import { types, flow } from "mobx-state-tree";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { validateShare } from "@/stores/documents/repository/shares";
import type {
  ShareDetails,
  ShareDocument,
  ShareFolderNode,
  TableDocument,
  BreadcrumbFolder,
} from "./models";
import type { Document } from "@/types/documents";
import type { TreeFolder } from "@/domains/documents/types";
import { ensureFileExtension, getTotalDocCount } from "./utils";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import {
  downloadSingleDocument,
  downloadFilesAsZip,
  collectAllFiles,
  collectFilesFromFolders,
  collectFilesFromIds,
  type ToastFn,
} from "./downloadService";

/**
 * Recursively sort folder nodes alphabetically (A–Z) by name so the share
 * folder tree matches the projects folder tree. The share API returns folders
 * in an arbitrary order; sorting here keeps the sidebar tree and the subfolder
 * table consistent.
 */
function sortFolderTree(nodes: readonly ShareFolderNode[]): ShareFolderNode[] {
  return [...nodes]
    .map((node) => ({ ...node, children: sortFolderTree(node.children) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const ShareStore = types
  .model("ShareStore", {
    // Share data from API (frozen - read only after loading)
    shareDetails: types.maybeNull(types.frozen<ShareDetails>()),

    // Loading state
    isLoading: types.optional(types.boolean, true),
    error: types.maybeNull(types.string),

    // Folder navigation
    currentFolderId: types.maybeNull(types.string),

    // Selection state
    selectedDocIds: types.map(types.boolean),
    selectedFolderIds: types.array(types.string),

    // Download state
    isDownloading: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    // Preview state - volatile because blob URLs need cleanup
    previewDocument: null as Document | null,
    previewBlobUrl: null as string | null,
    previewBlobData: null as Blob | null,
    previewOpen: false,
  }))
  // ============================================================================
  // Views
  // ============================================================================
  .views((self) => ({
    /** Check if share has folder structure */
    get hasFolders(): boolean {
      return Boolean(self.shareDetails?.folderTree && self.shareDetails.folderTree.length > 0);
    },

    /** Folder tree sorted alphabetically (A–Z) at every level */
    get sortedFolderTree(): ShareFolderNode[] {
      return self.shareDetails ? sortFolderTree(self.shareDetails.folderTree) : [];
    },

    /** Find a folder in the tree by ID */
    findFolderInTree(folderId: string, folders: ShareFolderNode[]): ShareFolderNode | null {
      for (const folder of folders) {
        if (folder.id === folderId) return folder;
        if (folder.children.length > 0) {
          const found = this.findFolderInTree(folderId, folder.children);
          if (found) return found;
        }
      }
      return null;
    },

    /** Get current folder node */
    get currentFolder(): ShareFolderNode | null {
      if (!self.currentFolderId || !self.shareDetails?.folderTree) return null;
      return this.findFolderInTree(self.currentFolderId, self.shareDetails.folderTree);
    },

    /** Build breadcrumb path from root to current folder */
    get breadcrumbPath(): BreadcrumbFolder[] {
      if (!self.currentFolderId || !self.shareDetails?.folderTree) return [];

      const path: BreadcrumbFolder[] = [];
      const findPath = (folderId: string, folders: ShareFolderNode[]): boolean => {
        for (const folder of folders) {
          if (folder.id === folderId) {
            path.push({ id: folder.id, name: folder.name });
            return true;
          }
          if (folder.children.length > 0 && findPath(folderId, folder.children)) {
            path.unshift({ id: folder.id, name: folder.name });
            return true;
          }
        }
        return false;
      };

      findPath(self.currentFolderId, self.shareDetails.folderTree);
      return path;
    },

    /** Get documents for current view */
    get displayDocuments(): ShareDocument[] {
      if (!self.shareDetails) return [];
      if (this.currentFolder) return this.currentFolder.documents;
      if (this.hasFolders) return self.shareDetails.rootDocuments || [];
      return self.shareDetails.documents;
    },

    /** Transform documents for DocumentListTable */
    get tableDocuments(): TableDocument[] {
      return this.displayDocuments.map((doc: ShareDocument) => ({
        id: String(doc.id),
        filename: doc.filename,
        file_path: "",
        building_id: "",
        content_type: doc.content_type,
        size: doc.size,
        created_at: doc.created_at,
        original_created_at: null,
        created_by_name: null,
        state: "COMPLETE" as const,
        version: null,
        version_group: null,
        parent_document_id: null,
      }));
    },

    /** Convert subfolders to TreeFolder format (sorted A–Z to match projects) */
    get currentSubfolders(): TreeFolder[] {
      if (!self.shareDetails?.folderTree) return [];

      const convert = (folder: ShareFolderNode, parentId: string | null): TreeFolder => ({
        id: folder.id,
        name: folder.name,
        parentId,
        category: "documents",
        building_id: 0,
        // Recursive total (own documents + all nested) so the subfolder table
        // rows match the sidebar tree and the header's API totals. Previously
        // folder.documents.length here counted direct children only, which made
        // the three surfaces disagree.
        documentCount: getTotalDocCount(folder),
        children: folder.children.map((c: ShareFolderNode) => convert(c, folder.id)),
      });

      // Resolve the current folder within the pre-sorted tree so subfolders
      // render alphabetically at every level.
      const sortedTree = this.sortedFolderTree;
      if (self.currentFolderId) {
        const current = this.findFolderInTree(self.currentFolderId, sortedTree);
        return (current?.children ?? []).map((c) => convert(c, self.currentFolderId));
      }
      return sortedTree.map((f) => convert(f, null));
    },

    /** Map of document ID to ShareDocument */
    get documentMap(): Map<string, ShareDocument> {
      if (!self.shareDetails) return new Map();
      return new Map(self.shareDetails.documents.map((doc) => [String(doc.id), doc]));
    },

    /** All document IDs */
    get allDocIds(): string[] {
      if (!self.shareDetails) return [];
      return self.shareDetails.documents.map((doc) => String(doc.id));
    },

    /** Selected document count */
    get selectedCount(): number {
      return Array.from(self.selectedDocIds.values()).filter(Boolean).length;
    },

    /** Check if document is selected */
    isSelected(id: string): boolean {
      return self.selectedDocIds.get(id) ?? false;
    },

    /** Check if any selection exists */
    get hasSelection(): boolean {
      return this.selectedCount > 0 || self.selectedFolderIds.length > 0;
    },

    /** Check if share expires within 24h */
    get isExpiringSoon(): boolean {
      if (!self.shareDetails) return false;
      const expiresAt = new Date(self.shareDetails.expiresAt);
      return expiresAt.getTime() - Date.now() < 24 * 60 * 60 * 1000;
    },

    /** Get expiration date */
    get expiresAt(): Date | null {
      if (!self.shareDetails) return null;
      return new Date(self.shareDetails.expiresAt);
    },

    // DocumentListTable stub interface
    get documents(): Map<string, unknown> {
      return new Map<string, unknown>();
    },
    get tags(): { folders: Map<string, unknown> } {
      return { folders: new Map<string, unknown>() };
    },
  }))
  // ============================================================================
  // Actions
  // ============================================================================
  .actions((self) => ({
    /** Load share by token */
    loadShare: flow(function* loadShare(token: string) {
      if (!/^[0-9a-f]{64}$/i.test(token)) {
        self.error = "Invalid share link format";
        self.isLoading = false;
        return;
      }

      self.isLoading = true;
      self.error = null;

      try {
        const details: ShareDetails = yield validateShare(token);
        self.shareDetails = details;
      } catch (err) {
        self.error = err instanceof Error ? err.message : "Failed to load shared documents";
      } finally {
        self.isLoading = false;
      }
    }),

    /** Navigate to folder */
    navigateToFolder(folderId: string | null) {
      self.currentFolderId = folderId;
    },

    /** Select/deselect a document */
    selectDocument(id: string, isSelected: boolean) {
      if (isSelected) {
        self.selectedDocIds.set(id, true);
      } else {
        self.selectedDocIds.delete(id);
      }
    },

    /** Select/deselect a folder */
    selectFolder(folderId: string, isSelected: boolean) {
      if (isSelected) {
        if (!self.selectedFolderIds.includes(folderId)) {
          self.selectedFolderIds.push(folderId);
        }
      } else {
        const index = self.selectedFolderIds.indexOf(folderId);
        if (index > -1) self.selectedFolderIds.splice(index, 1);
      }
    },

    /** Select/deselect all in current view */
    selectAll(isSelected: boolean) {
      if (isSelected) {
        self.tableDocuments.forEach((doc) => self.selectedDocIds.set(doc.id, true));
      } else {
        self.selectedDocIds.clear();
      }
    },

    /** Clear all selections */
    clearSelection() {
      self.selectedDocIds.clear();
      self.selectedFolderIds.clear();
    },

    /** Download a single document */
    downloadDocument: flow(function* downloadDocument(docId: string, toast: ToastFn) {
      const doc = self.documentMap.get(docId);
      if (!doc) return;
      yield downloadSingleDocument(doc, toast);
    }),

    /** Download selected documents (or all if none selected) */
    downloadSelected: flow(function* downloadSelected(toast: ToastFn) {
      if (!self.shareDetails) return;

      const selectedDocIds = Array.from(self.selectedDocIds.entries())
        .filter(([, selected]) => selected)
        .map(([id]) => id);

      const selectedFolders = [...self.selectedFolderIds];
      const hasFolderSelection = selectedFolders.length > 0;
      const hasDocSelection = selectedDocIds.length > 0;

      // No selection: download all
      if (!hasFolderSelection && !hasDocSelection) {
        self.isDownloading = true;
        toast({ title: "Preparing download", description: "Processing all files..." });

        const download = collectAllFiles(self.shareDetails);
        yield downloadFilesAsZip(download, self.shareDetails.title || "shared-documents", toast);

        self.isDownloading = false;
        return;
      }

      // Single document (no folders): direct download
      if (selectedDocIds.length === 1 && !hasFolderSelection) {
        const doc = self.documentMap.get(selectedDocIds[0]);
        if (doc) yield downloadSingleDocument(doc, toast);
        return;
      }

      // Folder or multiple items: always zip
      self.isDownloading = true;
      toast({ title: "Preparing download", description: "Processing selected files..." });

      // Collect files (and folder paths, so empty folders survive the zip)
      // from selected folders
      const folderDownload = hasFolderSelection
        ? collectFilesFromFolders(self.shareDetails.folderTree, selectedFolders)
        : { files: [], folderPaths: [] };

      // Collect individually selected documents (not already in folder selection)
      const docFiles = collectFilesFromIds(self.documentMap, selectedDocIds);

      const download = {
        files: [...folderDownload.files, ...docFiles],
        folderPaths: folderDownload.folderPaths,
      };

      // Determine zip filename based on selection
      let zipFilename: string;
      if (selectedFolders.length === 1 && !hasDocSelection) {
        // Single folder selected - use folder name
        const folder = self.findFolderInTree(selectedFolders[0], self.shareDetails.folderTree);
        zipFilename = folder?.name || self.shareDetails.title || "shared-documents";
      } else {
        // Multiple folders or mixed selection - use share title
        zipFilename = self.shareDetails.title || "shared-documents";
      }

      yield downloadFilesAsZip(download, zipFilename, toast);
      self.isDownloading = false;
    }),

    /** Open preview for a document */
    openPreview: flow(function* openPreview(docId: string, toast: ToastFn) {
      const doc = self.documentMap.get(docId);
      if (!doc?.signedUrl) return;

      const filename = ensureFileExtension(doc.filename, doc.content_type);

      // WHA-3604: an .ifc preview only opens a small "open in 3D viewer?" modal,
      // so there's nothing to render — skip the (potentially hundreds-of-MB)
      // blob fetch and open the dialog immediately with metadata only.
      if (isIfcFile(filename)) {
        self.previewDocument = {
          id: String(doc.id),
          filename,
          file_path: "",
          building_id: "",
          content_type: doc.content_type,
          size: doc.size,
          created_at: doc.created_at,
          parent_document_id: null,
        };
        self.previewBlobUrl = null;
        self.previewBlobData = null;
        self.previewOpen = true;
        return;
      }

      try {
        const response: Response = yield fetch(doc.signedUrl);
        if (!response.ok) throw new Error(`Preview failed: ${response.status}`);

        const arrayBuffer: ArrayBuffer = yield response.arrayBuffer();
        const blob = new Blob([arrayBuffer], {
          type: doc.content_type || "application/octet-stream",
        });
        const blobUrl = URL.createObjectURL(blob);

        self.previewDocument = {
          id: String(doc.id),
          filename,
          file_path: "",
          building_id: "",
          content_type: doc.content_type,
          size: doc.size,
          created_at: doc.created_at,
          parent_document_id: null,
        };
        self.previewBlobUrl = blobUrl;
        self.previewBlobData = doc.content_type?.startsWith("application/pdf") ? blob : null;
        self.previewOpen = true;
      } catch (err) {
        console.error("Preview failed:", err);
        toast({
          title: "Preview failed",
          description: err instanceof Error ? err.message : "Please try again",
          variant: "destructive",
        });
      }
    }),

    /** Close preview and cleanup */
    closePreview() {
      if (self.previewBlobUrl) URL.revokeObjectURL(self.previewBlobUrl);
      self.previewBlobUrl = null;
      self.previewBlobData = null;
      self.previewDocument = null;
      self.previewOpen = false;
    },

    /** Set preview open state */
    setPreviewOpen(open: boolean) {
      if (!open) {
        if (self.previewBlobUrl) URL.revokeObjectURL(self.previewBlobUrl);
        self.previewBlobUrl = null;
        self.previewBlobData = null;
        self.previewDocument = null;
      }
      self.previewOpen = open;
    },

    /** Dispose store */
    dispose() {
      if (self.previewBlobUrl) URL.revokeObjectURL(self.previewBlobUrl);
      self.previewBlobUrl = null;
      self.previewBlobData = null;
      self.previewDocument = null;
    },

    // DocumentListTable stubs
    loadDocuments: () => Promise.resolve(),
    loadTags: () => Promise.resolve(),
    refreshSingleDocument: () => Promise.resolve(),
    deleteDocument: () => Promise.resolve(),
    bulkMoveToTrash: () => Promise.resolve(),
  }));

// ============================================================================
// Type Exports
// ============================================================================

export type ShareStoreInstance = Instance<typeof ShareStore>;
export type ShareStoreSnapshotIn = SnapshotIn<typeof ShareStore>;
export type ShareStoreSnapshotOut = SnapshotOut<typeof ShareStore>;
